/* eslint-disable @typescript-eslint/no-explicit-any */

import { AnyFunction } from 'types';

interface Condition {
  is: any;
  then: any | (() => any);
}

type ConditionResult<T> = T extends () => infer R ? R : T;

export function cascadeEval<Cond extends Condition>(
  conditions: Cond[],
): ConditionResult<Cond['then']> {
  const firstTrue = conditions.find(({ is }) => is);
  const [lastCondition] = conditions.slice().reverse();

  const matchCondition = firstTrue ? firstTrue.then : lastCondition?.then;

  return (
    typeof matchCondition === 'function' ? (matchCondition as AnyFunction)?.() : matchCondition
  ) as ConditionResult<Cond>;
}

type Fn = () => any;

interface SwitchCondition<Ref> {
  is: Ref[] | Ref | true;
  then: any | Fn;
}

type SwitchResult<Then> = Fn extends Then
  ? ReturnType<Extract<Then, Fn>> | Exclude<Then, Fn>
  : Exclude<Then, Fn>;

/**
 * Perform an inline switch for quick logic branching
 *
 * Warning: result type may be incorrect with the current implementation.
 * use the slightly more verbose `switchIt` helper to proper inference
 */
export function quickSwitch<RefValue, Cond extends SwitchCondition<RefValue>>(
  ref: RefValue,
  conditions: Cond[],
): SwitchResult<Cond['then']> {
  const firstTrue = conditions.find(({ is }) =>
    Array.isArray(is) ? is.includes(ref) : ref === is,
  );

  const [lastCondition] = conditions.slice().reverse();

  const then = firstTrue?.then ?? lastCondition?.then;

  return (typeof then === 'function' ? (then as () => any)?.() : then) as Cond['then'];
}

/**
 * A "builder" version of the `quickSwitch` helper
 *
 * This version can accurately narrow `then` types
 */
export function switchIt<Ref>(ref: Ref) {
  return {
    cases<Cond extends SwitchCondition<Ref>>(conditions: Cond[]): SwitchResult<Cond['then']> {
      return quickSwitch(ref, conditions);
    },
  };
}
